import { Fragment, useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import FoundationSpResultCard from "./FoundationSpResultCard";

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
}

// Audit trail for every sp_insert_update_jobs call (bridge log rows with
// doc_type=job_sp). Click a row to see the full raw result.
export default function FoundationJobSpLog({ refreshKey, loader }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let alive = true;
    loader(50).then(({ data }) => { if (alive) setRows(data || []); }).catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [refreshKey, loader]);

  return (
    <div className="mt-4" data-testid="job-sp-log">
      <div className="text-xs font-semibold text-slate-600 mb-1">Bridge log — SP calls ({rows ? rows.length : "…"})</div>
      {rows === null ? (
        <div className="text-xs text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-slate-400" data-testid="job-sp-log-empty">No SP calls logged yet.</div>
      ) : (
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-2 py-1 w-4"></th>
                <th className="px-2 py-1">When</th><th className="px-2 py-1">User</th>
                <th className="px-2 py-1">DB</th><th className="px-2 py-1">job_no</th>
                <th className="px-2 py-1">Description</th><th className="px-2 py-1">Trunc</th>
                <th className="px-2 py-1">rc</th><th className="px-2 py-1">FSI</th>
                <th className="px-2 py-1">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Fragment key={r.id}>
                  <tr onClick={() => setOpen(open === r.id ? null : r.id)}
                      className="border-t border-slate-100 cursor-pointer hover:bg-slate-50"
                      data-testid={`job-sp-log-row-${r.id}`}>
                    <td className="px-2 py-1 text-slate-400">{open === r.id ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{fmt(r.created_at)}</td>
                    <td className="px-2 py-1 truncate max-w-[140px]">{r.operator}</td>
                    <td className="px-2 py-1 font-mono">{r.target_database || "—"}</td>
                    <td className="px-2 py-1 font-mono">{r.job_no}</td>
                    <td className="px-2 py-1 truncate max-w-[180px]">{r.description_sent}</td>
                    <td className="px-2 py-1">{r.truncated ? "yes" : "no"}</td>
                    <td className="px-2 py-1 font-mono">{r.return_code ?? "—"}</td>
                    <td className="px-2 py-1 font-mono">{r.fsi_error ?? "—"}</td>
                    <td className="px-2 py-1">
                      <span className={`px-1.5 py-0.5 rounded-full border font-medium ${
                        r.outcome === "success" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : r.outcome === "validation_error" ? "bg-amber-50 text-amber-700 border-amber-200"
                        : "bg-rose-50 text-rose-700 border-rose-200"}`}>{r.outcome}</span>
                    </td>
                  </tr>
                  {open === r.id && (
                    <tr className="bg-slate-50/60">
                      <td colSpan={10} className="px-3 py-2">
                        <FoundationSpResultCard result={r} testIdPrefix={`job-sp-log-${r.id}`} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
